import { useModuleRegistrationPayments } from '@/composables/queries/useModuleRegistrationPayments';
import { useStudentModuleElectives } from '@/composables/queries/useStudentModuleElectives';
import { resolveRegistrationUnitLimits } from '@/utils/registrationUnitLimits';
import { gql } from '@apollo/client/core';
import { useToast } from 'primevue/usetoast';
import { useLazyQuery, useMutation, useQuery } from '@vue/apollo-composable';
import { useUserStore } from '@/store/userStore';
import { useUserQuery } from '@/composables/queries/useUserQuery';
import { useCBTTests } from '@/composables/queries/cbt/useCBTTest';
import type { CourseRegistered, CourseRegistrationFinancialClearance } from '@/types/composition-functions';
import { GET_COURSE_REG_FINANCIAL_CLEARANCE } from '@/graphql/Queries/courseRegistration';
import { SUBMIT_COURSE_REGISTRATION, RESUBMIT_COURSE_REGISTRATION } from '@/graphql/mutations/courseRegistration';
import { semesterLabel } from '@/utils/semesterLabel';
import * as Sentry from '@sentry/vue';

// webonyx's wording for a variable that was sent as an explicit null; a key
// that is missing altogether says "was not provided" instead.
const NULL_VARIABLE_WORDING = 'must not be null';

function isNullVariableError(error: unknown): boolean {
  return error instanceof Error && error.message.includes(NULL_VARIABLE_WORDING);
}

// Mirrors FinancialClearanceGate::BLOCKED_MESSAGE on the backend, which also
// enforces this server-side; the client copy only saves a doomed round-trip.
export const FINANCIAL_CLEARANCE_BLOCKED_MESSAGE =
  'Course registration cannot be submitted. Please complete the required payment '
  + 'or ensure your scholarship has been approved before proceeding.';

// Mirrors CourseRegistrationAvailability::CLOSED_MESSAGE on the backend.
export const REGISTRATION_CLOSED_MESSAGE =
  'Course registration is not currently open. Please contact your school for more information.';

interface OutstandingCourseItem {
  programme_module_ids?: string[];
  course_id: string;
  course: { id: string; course_code: string; course_title: string; unit: number };
  curriculum_level: string;
  status: string;
  category: string;
  is_locked: boolean;
  unit: number;
}

interface OutstandingCoursesResult {
  compulsory: OutstandingCourseItem[];
  elective: OutstandingCourseItem[];
  compulsory_total_units: number;
  elective_total_units: number;
  passed_course_ids: string[];
  compulsory_mode?: string;
  enforce_prerequisites?: boolean;
}

// OutstandingCoursesResult.compulsory_mode is typed String! on the wire and
// carries the raw DB value (lowercase 'flexible'), unlike
// ProgrammeType.compulsory_mode which Lighthouse serialises as the uppercase
// enum case name — so comparing against CompulsoryModeEnum.Flexible ('FLEXIBLE')
// would never match here.
const COMPULSORY_MODE_FLEXIBLE = 'flexible';

export interface AvailableCourse {
  id: string;
  curriculum_id: string;
  course_code: string;
  course_title: string;
  unit: number;
  status: string;
  semester: number;
  isCompulsory: boolean;
  isAlreadyRegistered: boolean;
  isOutstanding: boolean;
  outstandingCategory: 'compulsory' | 'elective' | null;
  isLocked: boolean;
  originalLevel: string | null;
}

export function useCourseRegistration() {
  const toast = useToast();
  const userStore = useUserStore();

  const {
    currentClassroomSection,
    studentProfile,
    curriculum,
    loadingUser: loading,
    studentregcurrentsemester,
    studentAcademicCalendar,
    outstandingCourses,
    school,
    refetchUser,
    onResultUser,
  } = useUserQuery();

  // Master per-school switch: absent/true = registration open (today's
  // behaviour); only an explicit false closes it. Server enforces regardless.
  const isRegistrationClosed = computed(() => {
    const settings = school.value?.settings as { features?: { course_registration_enabled?: boolean } } | null | undefined;
    return settings?.features?.course_registration_enabled === false;
  });

  const usesModules = computed(() => ['MODULAR', 'RESIDENCE'].includes(studentAcademicCalendar.value?.programme?.delivery_mode ?? ''));
  const openModules = computed(() => studentProfile.value?.openProgrammeModules ?? []);
  const selectedModuleId = ref<string | null>(null);
  const selectedModuleIds = ref<string[]>([]);
  const moduleDrafts = ref<Record<string, AvailableCourse[]>>({});
  const modulePayments = useModuleRegistrationPayments(selectedModuleIds, usesModules);
  const moduleError = ref('');
  watch(openModules, (modules) => {
    selectedModuleIds.value = selectedModuleIds.value.filter(id => modules.some(module => module.id === id));
    if (!selectedModuleIds.value.length && modules[0]) selectedModuleIds.value = [modules[0].id];
    if (!modules.some(m => m.id === selectedModuleId.value)) selectedModuleId.value = modules[0]?.id ?? null;
  }, { immediate: true });

  const filters = ref({ global: { value: null, matchMode: 'contains' } });

  const effectiveUnitLimits = computed(() => resolveRegistrationUnitLimits(
    studentAcademicCalendar.value,
    studentAcademicCalendar.value?.programme,
    usesModules.value ? openModules.value.find(module => module.id === selectedModuleId.value) : null,
  ));
  const minUnitsAllowed = computed(() => effectiveUnitLimits.value.min.value);
  const maxUnitsAllowed = computed(() => effectiveUnitLimits.value.max.value);
  const unitLimitConfigurationError = computed(() => minUnitsAllowed.value > maxUnitsAllowed.value
    ? 'The effective minimum units exceed the maximum. Contact your school to correct the registration limits.' : '');

  const submitting = ref(false);
  const error = ref('');
  const canRegister = ref(true);
  const isResubmitting = ref(false);
  const alreadyRegisteredCourses = ref<CourseRegistered[]>([]);
  const availableCourses = ref<AvailableCourse[]>([]);
  const selectedCourses = ref<AvailableCourse[]>([]);
  const showRegisteredCourses = ref(false);
  const page = ref(1);
  const rows = ref(10);

  const hasRegisteredCurrentSemester = computed(() => studentregcurrentsemester.value && studentregcurrentsemester.value.coursesRegistered?.length > 0);

  const variables = computed(() => ({ first: rows.value, page: page.value }));
  const { refetch } = useCBTTests({ autoFetch: true, variables });

  const registeredCoursesFromAPI = computed(() => studentregcurrentsemester.value?.coursesRegistered || []);

  const registeredSemesterLabel = computed(() =>
    semesterLabel(studentregcurrentsemester.value?.semester),
  );

  const currentSemester = computed(() => userStore.schoolActiveSettings?.activeSetting?.termName || 'Current');

  const totalUnits = computed(() => selectedCourses.value.reduce((sum, course) => sum + (Number(course.unit) || 0), 0));
  const registeredTotalUnits = computed(() => alreadyRegisteredCourses.value.reduce((sum, item) => sum + (Number(item.course?.unit) || 0), 0));
  const totalAvailableCourses = computed(() => availableCourses.value.length);
  const totalAvailableUnits = computed(() => availableCourses.value.reduce((sum, course) => sum + (Number(course.unit) || 0), 0));
  const hasPreviouslyRegistered = computed(() => registeredCoursesFromAPI.value.length > 0);

  const outstandingData = computed<OutstandingCoursesResult | null>(() => outstandingCourses.value ?? null);
  const compulsoryOutstandingUnits = computed(() => usesModules.value
    ? availableCourses.value.filter(course => course.outstandingCategory === 'compulsory').reduce((sum, course) => sum + course.unit, 0)
    : (outstandingData.value?.compulsory ?? []).reduce((sum, course) => sum + course.unit, 0));
  const passedCourseIds = computed(() => new Set(outstandingData.value?.passed_course_ids ?? []));

  const currentLevelCompulsoryOverflow = computed(() => {
    if (!outstandingData.value || maxUnitsAllowed.value === Infinity) return false;
    const currentCompulsoryUnits = availableCourses.value
      .filter(c => !c.isOutstanding && c.isCompulsory)
      .reduce((sum, c) => sum + c.unit, 0);
    return compulsoryOutstandingUnits.value + currentCompulsoryUnits > maxUnitsAllowed.value;
  });

  const { mutate: submitReg } = useMutation(SUBMIT_COURSE_REGISTRATION);
  const { mutate: resubmitReg } = useMutation(RESUBMIT_COURSE_REGISTRATION);

  // Per-school financial clearance gate: when the school requires it and the
  // student still owes, the backend rejects submit/resubmit — this powers the
  // banner and disables the buttons before that round-trip.
  const { result: clearanceResult, refetch: refetchClearance } = useQuery(
    GET_COURSE_REG_FINANCIAL_CLEARANCE,
    null,
    { fetchPolicy: 'network-only' },
  );
  const financialClearance = computed<CourseRegistrationFinancialClearance | null>(
    () => clearanceResult.value?.getCourseRegistrationFinancialClearance ?? null,
  );
  const isBlockedByFinancialClearance = computed(
    () => !!financialClearance.value?.required && !financialClearance.value?.cleared,
  );
  const clearanceOutstandingBalance = computed(
    () => Number(financialClearance.value?.outstanding_balance ?? 0),
  );
  const clearancePegs = computed(() => financialClearance.value?.pegs ?? []);
  const unmetClearancePegs = computed(() => clearancePegs.value.filter(peg => !peg.cleared));

  async function checkRegistrationStatus() {
    if (hasRegisteredCurrentSemester.value) {
      canRegister.value = false;
      alreadyRegisteredCourses.value = registeredCoursesFromAPI.value;
    } else {
      canRegister.value = true;
      alreadyRegisteredCourses.value = [];
    }
  }

  function ownedByAnotherSelectedModule(courseId: string, moduleId: string | null) {
    const registered = registeredCoursesFromAPI.value.find(row => row.course?.id === courseId);
    if (registered?.programme_module_id) return registered.programme_module_id !== moduleId;
    const offeredIds = new Set<string>();
    for (const row of curriculum.value ?? []) {
      if (row?.course?.id !== courseId) continue;
      if (row.programme_module_id) offeredIds.add(row.programme_module_id);
      for (const module of row.moduleOfferings ?? []) offeredIds.add(module.id);
    }
    for (const row of [...outstandingData.value?.compulsory ?? [], ...outstandingData.value?.elective ?? []]) {
      if (row.course_id === courseId) for (const id of row.programme_module_ids ?? []) offeredIds.add(id);
    }
    const owner = selectedModuleIds.value.find(id => offeredIds.has(id));
    return !!owner && owner !== moduleId;
  }

  function coursesForModule(moduleId = selectedModuleId.value) {
    const otherModuleCourseIds = new Set(usesModules.value ? registeredCoursesFromAPI.value
      .filter(row => row.programme_module_id !== moduleId).map(row => row.course_id) : []);
    const registeredIds = new Set(registeredCoursesFromAPI.value.filter(r => !usesModules.value || r.programme_module_id === moduleId).flatMap((r) => r.course ? [r.course.id] : []));
    const courseMap = new Map<string, AvailableCourse>();
    const isFlexible = outstandingData.value?.compulsory_mode === COMPULSORY_MODE_FLEXIBLE;

    if (outstandingData.value) {
      for (const item of outstandingData.value.compulsory) {
        if (usesModules.value && !item.programme_module_ids?.includes(moduleId ?? '')) continue;
        courseMap.set(item.course.id, {
          id: item.course.id,
          curriculum_id: '',
          course_code: item.course.course_code,
          course_title: item.course.course_title,
          unit: item.course.unit,
          status: item.status,
          semester: 0,
          isCompulsory: true,
          isAlreadyRegistered: registeredIds.has(item.course.id),
          isOutstanding: true,
          outstandingCategory: 'compulsory',
          isLocked: !isFlexible,
          originalLevel: item.curriculum_level,
        });
      }

      for (const item of outstandingData.value.elective) {
        if (usesModules.value && !item.programme_module_ids?.includes(moduleId ?? '')) continue;
        if (!courseMap.has(item.course.id)) {
          courseMap.set(item.course.id, {
            id: item.course.id,
            curriculum_id: '',
            course_code: item.course.course_code,
            course_title: item.course.course_title,
            unit: item.course.unit,
            status: item.status,
            semester: 0,
            isCompulsory: false,
            isAlreadyRegistered: registeredIds.has(item.course.id),
            isOutstanding: true,
            outstandingCategory: 'elective',
            isLocked: false,
            originalLevel: item.curriculum_level,
          });
        }
      }
    }

    if (curriculum.value && curriculum.value.length > 0) {
      for (const c of curriculum.value) {
        if (!c?.course) continue;
        if (usesModules.value && c.programme_module_id !== moduleId && !c.moduleOfferings?.some(m => m.id === moduleId)) continue;
        if (!courseMap.has(c.course.id) && !passedCourseIds.value.has(c.course.id)) {
          courseMap.set(c.course.id, {
            id: c.course.id,
            curriculum_id: c.id,
            course_code: c.course.course_code,
            course_title: c.course.course_title,
            unit: c.course.unit,
            status: c.status,
            semester: c.semester,
            isCompulsory: c.status === 'C',
            isAlreadyRegistered: registeredIds.has(c.course.id),
            isOutstanding: false,
            outstandingCategory: null,
            isLocked: false,
            originalLevel: null,
          });
        }
      }
    }

    for (const courseId of otherModuleCourseIds) courseMap.delete(courseId);
    if (usesModules.value) {
      for (const courseId of courseMap.keys()) if (ownedByAnotherSelectedModule(courseId, moduleId)) courseMap.delete(courseId);
    }
    const available = Array.from(courseMap.values());

    const locked = available.filter(c => c.isLocked);
    const currentCompulsory = available.filter(c => !c.isOutstanding && c.isCompulsory);
    const alreadyReg = available.filter(c => c.isAlreadyRegistered);

    // Include previously registered courses not in curriculum/outstanding as extra electives
    const previouslyRegisteredExtras: AvailableCourse[] = [];
    for (const reg of registeredCoursesFromAPI.value) {
      if (usesModules.value && reg.programme_module_id !== moduleId) continue;
      if (!reg.course) continue;
      if (!courseMap.has(reg.course.id) && !passedCourseIds.value.has(reg.course.id)) {
        const extra: AvailableCourse = {
          id: reg.course.id,
          curriculum_id: '',
          course_code: reg.course.course_code,
          course_title: reg.course.course_title,
          unit: reg.course.unit,
          status: reg.course_status ?? 'E',
          semester: 0,
          isCompulsory: usesModules.value && reg.course_status === 'C',
          isAlreadyRegistered: true,
          isOutstanding: false,
          outstandingCategory: null,
          isLocked: usesModules.value && reg.course_status === 'C' && !isFlexible,
          originalLevel: null,
        };
        if (extra.isCompulsory) available.push(extra);
        previouslyRegisteredExtras.push(extra);
      }
    }

    const selected = [
      ...new Map([...locked, ...currentCompulsory, ...alreadyReg, ...previouslyRegisteredExtras].map(c => [c.id, c])).values(),
    ];
    return { available, selected };
  }

  function buildAvailableCourses() {
    moduleDrafts.value = {};
    for (const id of selectedModuleIds.value) moduleDrafts.value[id] = coursesForModule(id).selected;
    const courses = coursesForModule();
    availableCourses.value = courses.available;
    selectedCourses.value = courses.selected;
  }

  watch(selectedCourses, (courses) => {
    if (usesModules.value && selectedModuleId.value) moduleDrafts.value[selectedModuleId.value] = courses;
  }, { flush: 'sync' });
  watch(selectedModuleIds, (ids) => {
    for (const id of ids) {
      const initial = coursesForModule(id).selected;
      const retained = moduleDrafts.value[id]?.filter(course => !ownedByAnotherSelectedModule(course.id, id));
      moduleDrafts.value[id] = retained
        ? [...new Map([...initial.filter(course => course.isCompulsory), ...retained].map(course => [course.id, course])).values()]
        : initial;
    }
    if (selectedModuleId.value && ids.includes(selectedModuleId.value)) {
      availableCourses.value = coursesForModule().available;
      selectedCourses.value = moduleDrafts.value[selectedModuleId.value] ?? [];
    }
    if (!ids.includes(selectedModuleId.value ?? '')) selectedModuleId.value = ids[0] ?? null;
  });
  watch(selectedModuleId, () => {
    const courses = coursesForModule();
    availableCourses.value = courses.available;
    selectedCourses.value = moduleDrafts.value[selectedModuleId.value ?? ''] ?? courses.selected;
  });

  const moduleSelections = computed(() => selectedModuleIds.value.map(id => {
    const module = openModules.value.find(item => item.id === id);
    const courses = id === selectedModuleId.value ? selectedCourses.value : moduleDrafts.value[id] ?? [];
    const limits = resolveRegistrationUnitLimits(studentAcademicCalendar.value, studentAcademicCalendar.value?.programme, module);
    const units = courses.reduce((sum, course) => sum + course.unit, 0);
    return { id, name: module?.name ?? id, courses, units, limits };
  }));

  onResultUser(() => {
    checkRegistrationStatus();
    buildAvailableCourses();
  });

  async function fetchData() {
    error.value = '';
    refetchUser();
  }

  async function submitRegistration() {
    if (unitLimitConfigurationError.value) {
      moduleError.value = unitLimitConfigurationError.value;
      return;
    }
    moduleError.value = '';
    submitting.value = true;
    if (usesModules.value && !openModules.value.some(m => m.id === selectedModuleId.value)) {
      moduleError.value = 'Select an open module in this semester.';
      submitting.value = false;
      return;
    }

    if (isRegistrationClosed.value) {
      toast.add({ severity: 'warn', summary: 'Registration Closed', detail: REGISTRATION_CLOSED_MESSAGE, life: 6000 });
      submitting.value = false;
      return;
    }

    if (isBlockedByFinancialClearance.value) {
      toast.add({ severity: 'warn', summary: 'Financial Clearance Required', detail: FINANCIAL_CLEARANCE_BLOCKED_MESSAGE, life: 6000 });
      submitting.value = false;
      return;
    }

    if (totalUnits.value < minUnitsAllowed.value) {
      toast.add({ severity: 'error', summary: 'Insufficient Units', detail: `You must register at least ${minUnitsAllowed.value} units.`, life: 3000 });
      submitting.value = false;
      return;
    }

    if (totalUnits.value > maxUnitsAllowed.value) {
      toast.add({ severity: 'error', summary: 'Too Many Units', detail: `You cannot exceed ${maxUnitsAllowed.value} units.`, life: 3000 });
      submitting.value = false;
      return;
    }

    const flexibleMode = outstandingData.value?.compulsory_mode === COMPULSORY_MODE_FLEXIBLE;

    if (!flexibleMode) {
      const missingOutstanding = availableCourses.value.filter(
        c => c.isLocked && !selectedCourses.value.some(s => s.id === c.id),
      );
      if (missingOutstanding.length > 0) {
        const codes = missingOutstanding.map(c => c.course_code).join(', ');
        toast.add({ severity: 'error', summary: 'Missing Outstanding Courses', detail: `You must register for: ${codes}`, life: 5000 });
        submitting.value = false;
        return;
      }

      const currentLevelCompulsory = currentLevelCourses.value.filter(c => c.isCompulsory);
      const missingCurrentCompulsory = currentLevelCompulsory.filter(
        c => !selectedCourses.value.some(s => s.id === c.id),
      );
      if (missingCurrentCompulsory.length > 0) {
        const codes = missingCurrentCompulsory.map(c => c.course_code).join(', ');
        toast.add({
          severity: 'error',
          summary: 'Missing Compulsory Courses',
          detail: `All current-level compulsory courses must be selected. Missing: ${codes}. If your unit budget is insufficient, request additional units from your admin.`,
          life: 8000,
        });
        submitting.value = false;
        return;
      }
    }

    try {
      if (usesModules.value) {
        const seen = new Set<string>();
        for (const selection of moduleSelections.value) {
          if (!selection.courses.length || selection.units < selection.limits.min.value || selection.units > selection.limits.max.value) {
            throw new Error(`${selection.name}: select courses within its minimum and maximum unit limits.`);
          }
          for (const course of selection.courses) {
            if (seen.has(course.id)) throw new Error(`${course.course_code} is selected in more than one module. Keep it in one module only.`);
            seen.add(course.id);
          }
        }
        if (outstandingData.value?.compulsory_mode !== COMPULSORY_MODE_FLEXIBLE) {
          for (const selection of moduleSelections.value) {
            const missing = coursesForModule(selection.id).available.filter(course => course.isCompulsory && !seen.has(course.id));
            if (missing.length) throw new Error(`${selection.name}: register all compulsory courses: ${missing.map(course => course.course_code).join(', ')}.`);
          }
        }
        const payment = await modulePayments.refetch();
        if (!payment?.data?.moduleRegistrationPayments.cleared) {
          throw new Error('Complete payment for the selected modules before submitting registration.');
        }
      }
      const coursesPayload = selectedCourses.value.map(course => ({
        course_id: course.id,
        status: course.isCompulsory || course.outstandingCategory === 'compulsory' ? 'C' : 'E',
      }));

      const input = {
        courses: coursesPayload,
        programme_module_id: usesModules.value ? selectedModuleId.value : undefined,
        modules: usesModules.value ? moduleSelections.value.map(selection => ({
          programme_module_id: selection.id,
          courses: selection.courses.map(course => ({ course_id: course.id, status: course.isCompulsory ? 'C' : 'E' })),
        })) : undefined,
      };
      const result = hasPreviouslyRegistered.value
        ? await resubmitReg({ input })
        : await submitReg({ input });

      await refetch();
      await refetchUser();

      const responseData = isResubmitting.value
        ? result?.data?.resubmitCourseRegistration
        : result?.data?.submitCourseRegistration;

      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: isResubmitting.value ? 'Courses re-submitted successfully' : 'Courses registered successfully',
        life: 3000,
      });

      if (responseData?.coursesRegistered && Array.isArray(responseData.coursesRegistered)) {
        alreadyRegisteredCourses.value = responseData.coursesRegistered;
      }

      canRegister.value = false;
      isResubmitting.value = false;
      selectedCourses.value = [];

      await refetchUser();
      await checkRegistrationStatus();
    } catch (e) {
      const gqlError = e as { message?: string; graphQLErrors?: Array<{ message?: string }> };
      if (gqlError.message?.includes('already exists') || gqlError.graphQLErrors?.[0]?.message?.includes('already exists')) {
        toast.add({ severity: 'warn', summary: 'Already Registered', detail: 'You already registered. You can now re-submit your courses.', life: 4000 });
        isResubmitting.value = true;
        canRegister.value = false;
        return;
      }
      // Backend financial-clearance rejection (e.g. balance changed after this
      // page loaded) — surface the policy message and refresh the banner state.
      if (gqlError.message?.includes('scholarship has been approved') || gqlError.graphQLErrors?.[0]?.message?.includes('scholarship has been approved')) {
        toast.add({ severity: 'warn', summary: 'Financial Clearance Required', detail: FINANCIAL_CLEARANCE_BLOCKED_MESSAGE, life: 6000 });
        refetchClearance()?.catch(() => undefined);
        return;
      }
      moduleError.value = e instanceof Error ? e.message : 'Something went wrong';
      toast.add({ severity: 'error', summary: 'Error', detail: moduleError.value, life: 6000 });
    } finally {
      submitting.value = false;
    }
  }

  function enableResubmit() {
    isResubmitting.value = true;
    canRegister.value = true;
    buildAvailableCourses();
  }

  function cancelResubmit() {
    isResubmitting.value = false;
    canRegister.value = false;
  }

  function viewRegisteredCourses() {
    showRegisteredCourses.value = true;
  }

  const outstandingCompulsoryCourses = computed(() =>
    availableCourses.value.filter(c => c.isOutstanding && c.outstandingCategory === 'compulsory'),
  );
  const outstandingElectiveCourses = computed(() =>
    availableCourses.value.filter(c => c.isOutstanding && c.outstandingCategory === 'elective'),
  );
  const currentLevelCourses = computed(() =>
    availableCourses.value.filter(c => !c.isOutstanding),
  );
  const hasOutstandingCourses = computed(() =>
    outstandingCompulsoryCourses.value.length > 0 || outstandingElectiveCourses.value.length > 0,
  );
  const outstandingTotalUnits = computed(() =>
    [...outstandingCompulsoryCourses.value, ...outstandingElectiveCourses.value]
      .filter(c => selectedCourses.value.some(s => s.id === c.id))
      .reduce((sum, c) => sum + c.unit, 0),
  );

  // Extra elective course search (student-safe, no admin policy)
  const SEARCH_COURSES = gql`
    query SearchSchoolCourses($query: String, $first: Int) {
      searchSchoolCourses(query: $query, first: $first) {
        id course_code course_title unit
      }
    }
  `;
  const extraSearchQuery = ref('');
  const { courses: additionalModuleCourses, loading: loadingModuleElectives, error: moduleElectiveSearchError } = useStudentModuleElectives(selectedModuleId, usesModules, extraSearchQuery);
  // The variable is deliberately nullable and the server defaults null to
  // "no filter": clients in the field kept producing a null here through a
  // path nobody has located, and with `$query: String!` that null was a
  // request-killing validation error (Sentry JAVASCRIPT-VUE-H/46). The
  // `?? ''` stays as first line of defence.
  const searchVariables = () => ({ query: extraSearchQuery.value ?? '', first: 50 });
  const { result: searchResult, load: loadSearch, refetch: refetchSearch } = useLazyQuery(SEARCH_COURSES,
    searchVariables,
    { fetchPolicy: 'network-only' },
  );
  const searchableCourses = computed(() => searchResult.value?.searchSchoolCourses ?? []);

  const extraElectiveId = ref<string | null>(null);

  // `loadSearch()` returns a Promise on its FIRST call only (@vue/apollo-composable
  // 4.2.2) which rejects on any query error — timeout, 5xx, expired auth. Left
  // uncaught it escapes as an unhandled rejection and pages Sentry
  // (JAVASCRIPT-VUE-46, culprit `new Promise(<anonymous>)`). Every later call
  // returns `false`, which is the caller's signal to refetch instead.
  function startSearch(): boolean {
    const started = loadSearch();
    if (!started) return false;
    void Promise.resolve(started).catch(reportSearchFailure);
    return true;
  }

  // A transient failure here — offline, a 5xx, an expired session — is not
  // worth a report: the picker stays empty until the next keystroke retries.
  // The `$query` null validation error is, and only with the variables that
  // were actually on the wire: the server answers a validation error with
  // HTTP 200, so nothing backend-side records them, which is why
  // JAVASCRIPT-VUE-46 went undiagnosed for as long as it did.
  function reportSearchFailure(error: unknown): void {
    if (!isNullVariableError(error)) return;
    Sentry.withScope((scope) => {
      scope.setContext('searchVariables', { ...searchVariables(), rawQuery: extraSearchQuery.value });
      Sentry.captureException(error);
    });
  }

  startSearch();

  let extraDebounce: ReturnType<typeof setTimeout> | null = null;
  function onExtraCourseFilter(event: { originalEvent: Event; value: string | null }) {
    if (extraDebounce) clearTimeout(extraDebounce);
    extraDebounce = setTimeout(() => {
      extraSearchQuery.value = event?.value ?? '';
      if (usesModules.value) return;
      if (!startSearch()) refetchSearch()?.catch(() => undefined);
    }, 300);
  }

  // A debounce firing after the page is left would refetch against a
  // disposed query scope — drop it with the component.
  onScopeDispose(() => {
    if (extraDebounce) clearTimeout(extraDebounce);
  });

  const moduleElectiveOptions = computed(() => {
    const selectedIds = new Set(selectedCourses.value.map(course => course.id));
    const earlierModuleIds = new Set(registeredCoursesFromAPI.value
      .filter(course => course.programme_module_id !== selectedModuleId.value)
      .flatMap(course => course.course ? [course.course.id] : []));
    const query = extraSearchQuery.value.trim().toLowerCase();
    const additional: AvailableCourse[] = additionalModuleCourses.value.map(course => ({
      id: course.id, curriculum_id: '', course_code: course.course_code, course_title: course.course_title,
      unit: course.unit, status: 'E', semester: 0, isCompulsory: false, isAlreadyRegistered: false,
      isOutstanding: false, outstandingCategory: null, isLocked: false, originalLevel: null,
    }));
    const candidates = new Map(additional.map(course => [course.id, course]));
    for (const course of availableCourses.value) candidates.set(course.id, course);
    return [...candidates.values()].filter(course => !course.isCompulsory
      && !selectedIds.has(course.id) && !earlierModuleIds.has(course.id) && !passedCourseIds.value.has(course.id)
      && `${course.course_code} ${course.course_title}`.toLowerCase().includes(query));
  });

  watch(selectedModuleId, () => {
    extraElectiveId.value = null;
    extraSearchQuery.value = '';
    if (extraDebounce) clearTimeout(extraDebounce);
  });

  const extraCourseOptions = computed<{ label: string; value: string }[]>(() => {
    if (usesModules.value) {
      return moduleElectiveOptions.value.map(course => ({
        label: `${course.course_code} — ${course.course_title} (${course.unit}u)`, value: course.id,
      }));
    }
    const existingIds = new Set(availableCourses.value.map(c => c.id));
    const selectedIds = new Set(selectedCourses.value.map(c => c.id));
    return searchableCourses.value
      .filter((c: { id: string }) => !existingIds.has(c.id) && !selectedIds.has(c.id) && !passedCourseIds.value.has(c.id))
      .map((c: { id: string; course_code: string; course_title: string; unit: number }) => ({
        label: `${c.course_code} — ${c.course_title} (${c.unit}u)`,
        value: c.id,
      }));
  });

  const extraElectives = computed(() =>
    selectedCourses.value.filter(c => !c.isOutstanding && !availableCourses.value.some(a => a.id === c.id)),
  );

  function removeExtraElective(courseId: string) {
    selectedCourses.value = selectedCourses.value.filter(c => c.id !== courseId);
  }

  function addExtraElective() {
    if (!extraElectiveId.value) return;
    if (usesModules.value) {
      const course = moduleElectiveOptions.value.find(item => item.id === extraElectiveId.value);
      if (course) selectedCourses.value = [...selectedCourses.value, course];
      extraElectiveId.value = null;
      return;
    }
    const course = searchableCourses.value.find((c: { id: string }) => c.id === extraElectiveId.value);
    if (!course) return;

    const newCourse: AvailableCourse = {
      id: course.id,
      curriculum_id: '',
      course_code: course.course_code,
      course_title: course.course_title,
      unit: course.unit,
      status: 'E',
      semester: 0,
      isCompulsory: false,
      isAlreadyRegistered: false,
      isOutstanding: false,
      outstandingCategory: null,
      isLocked: false,
      originalLevel: null,
    };

    selectedCourses.value = [...selectedCourses.value, newCourse];
    extraElectiveId.value = null;
  }

  function toggleElectiveOutstanding(course: AvailableCourse, selected: boolean) {
    if (selected) {
      if (!selectedCourses.value.some(c => c.id === course.id)) {
        selectedCourses.value = [...selectedCourses.value, course];
      }
    } else {
      selectedCourses.value = selectedCourses.value.filter(c => c.id !== course.id);
    }
  }

  function toggleCurrentLevel(course: AvailableCourse, selected: boolean) {
    if (selected) {
      if (!selectedCourses.value.some(c => c.id === course.id)) {
        selectedCourses.value = [...selectedCourses.value, course];
      }
    } else {
      selectedCourses.value = selectedCourses.value.filter(c => c.id !== course.id);
    }
  }

  watch(availableCourses, (courses) => {
    const locked = courses.filter(c => c.isLocked);
    selectedCourses.value = [
      ...new Map([...selectedCourses.value, ...locked].map(item => [item.id, item])).values(),
    ];
  }, { immediate: true });

  return {
    loadingModuleElectives, moduleElectiveSearchError,
    selectedModuleIds, moduleSelections, modulePayments,
    usesModules, openModules, selectedModuleId, moduleError, effectiveUnitLimits, unitLimitConfigurationError,
    currentClassroomSection,
    studentProfile,
    curriculum,
    studentAcademicCalendar,

    filters,
    minUnitsAllowed,
    maxUnitsAllowed,
    loading,
    submitting,
    error,
    canRegister,
    isResubmitting,
    alreadyRegisteredCourses,
    availableCourses,
    selectedCourses,
    showRegisteredCourses,
    page,
    rows,

    hasRegisteredCurrentSemester,
    registeredCoursesFromAPI,
    registeredSemesterLabel,
    currentSemester,
    totalUnits,
    registeredTotalUnits,
    totalAvailableCourses,
    totalAvailableUnits,
    hasPreviouslyRegistered,
    outstandingData,
    compulsoryOutstandingUnits,
    currentLevelCompulsoryOverflow,
    financialClearance,
    isBlockedByFinancialClearance,
    clearanceOutstandingBalance,
    clearancePegs,
    unmetClearancePegs,
    isRegistrationClosed,
    outstandingCompulsoryCourses,
    outstandingElectiveCourses,
    currentLevelCourses,
    hasOutstandingCourses,
    outstandingTotalUnits,

    fetchData,
    submitRegistration,
    enableResubmit,
    cancelResubmit,
    viewRegisteredCourses,
    toggleElectiveOutstanding,
    toggleCurrentLevel,
    extraElectiveId,
    extraCourseOptions,
    onExtraCourseFilter,
    addExtraElective,
    extraElectives,
    removeExtraElective,
  };
}
